// timelineViewUtils.js

export const DAY_MS = 24 * 60 * 60 * 1000;

export const getInclusiveEndOffsetMs = (fieldType) => {
    const type = String(fieldType || '').toLowerCase();
    return type === 'date' || type === 'daterange' ? DAY_MS : 0;
};

export const getUnscheduledLabel = (timelineFieldDef) => {
    const fieldLabel = String(timelineFieldDef?.label || '').trim();
    if (!fieldLabel) return 'No timeline date set';
    return `No ${fieldLabel} set`;
};

export const getScheduledRows = (timelineRows = []) => (
    (Array.isArray(timelineRows) ? timelineRows : [])
        .filter((row) => row?.type === 'item' && row?.entry?.range?.start && row?.entry?.range?.end)
);

export const parseScheduleWindowViewport = (scheduleWindow) => {
  if (!scheduleWindow || typeof scheduleWindow !== 'object') return null;

  const windowStartMs = Number(scheduleWindow.windowStartMs);
  const windowEndMs = Number(scheduleWindow.windowEndMs);
  if (!Number.isFinite(windowStartMs) || !Number.isFinite(windowEndMs)) return null;
  if (windowEndMs < windowStartMs) return null;

  return {
    start: new Date(windowStartMs),
    end: new Date(windowEndMs),
  };
};

export const resolveViewportTimelineBounds = (viewportBounds) => {
  if (!viewportBounds?.start || !viewportBounds?.end) return null;

  return {
    start: new Date(viewportBounds.start),
    end: new Date(viewportBounds.end),
  };
};

export const getScheduledBounds = (scheduledRows = []) => {
    if (!Array.isArray(scheduledRows) || scheduledRows.length === 0) return null;

    let minStart = scheduledRows[0].entry.range.start;
    let maxEnd = scheduledRows[0].entry.range.end;

    scheduledRows.forEach((row) => {
        const start = row.entry.range.start;
        const end = row.entry.range.end;
        if (start.getTime() < minStart.getTime()) minStart = start;
        if (end.getTime() > maxEnd.getTime()) maxEnd = end;
    });

    return { minStart, maxEnd };
};

export const formatTimelineRangeText = (range, fieldType) => {
    if (!range?.start || !range?.end) return '';

    const type = String(fieldType || '').toLowerCase();
    const includeTime = type === 'datetime' || type === 'datetimerange';
    const formatOptions = includeTime
        ? {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        }
        : {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        };

    const startLabel = range.start.toLocaleDateString('en-US', formatOptions);
    const endLabel = range.end.toLocaleDateString('en-US', formatOptions);
    if (range.isMilestone || startLabel === endLabel) return startLabel;
    return `${startLabel} → ${endLabel}`;
};

export const buildTopHeaderSegments = (columns = [], { labelKey = 'topLabel', keyPrefix = 'seg' } = {}) => {
    if (!Array.isArray(columns) || columns.length === 0) return [];

    const segments = [];
    let current = {
        key: `${keyPrefix}_${columns[0].key}`,
        label: columns[0][labelKey],
        span: 1,
    };

    for (let i = 1; i < columns.length; i += 1) {
        const column = columns[i];
        if (column[labelKey] === current.label) {
            current.span += 1;
        } else {
            segments.push(current);
            current = {
                key: `${keyPrefix}_${column.key}`,
                label: column[labelKey],
                span: 1,
            };
        }
    }

    segments.push(current);
    return segments;
};

export const getTimelineBarStyle = ({
    entry,
    bounds,
    spanMs,
    trackWidth,
    minDurationMs = DAY_MS,
    minWidthPercent = 1.1,
    inclusiveEndMs = 0,
}) => {
    if (!bounds || !entry?.range) return null;

    const barStart = entry.range.start.getTime();
    const barEnd = entry.range.end.getTime();
    const startOffset = Math.max(0, barStart - bounds.start.getTime());
    const endOffset = Math.min(spanMs, (barEnd - bounds.start.getTime()) + inclusiveEndMs);
    const leftPercent = (startOffset / spanMs) * 100;
    const widthRaw = ((Math.max(startOffset + minDurationMs, endOffset) - startOffset) / spanMs) * 100;
    const widthPercent = Math.max(widthRaw, minWidthPercent);
    const clampedWidthPercent = Math.min(100 - leftPercent, widthPercent);
    const leftPx = (leftPercent / 100) * trackWidth;
    const widthPx = (clampedWidthPercent / 100) * trackWidth;

    return {
        left: `${leftPercent}%`,
        width: `${clampedWidthPercent}%`,
        leftPercent,
        widthPercent: clampedWidthPercent,
        leftPx,
        widthPx,
        rightPx: leftPx + widthPx,
    };
};

/**
 * Build SVG dependency paths (predecessor bar end → successor bar start).
 *
 * @param {Array<{ key: string, entry: object, bar: object, rowCenterY: number }>} barItems
 * @returns {Array<{ key: string, d: string }>}
 */
export const buildTimelineDependencyPaths = (barItems = []) => {
    const byKey = new Map();
    (Array.isArray(barItems) ? barItems : []).forEach((item) => {
        if (item?.key && item?.bar) byKey.set(item.key, item);
    });

    const paths = [];
    byKey.forEach((successor) => {
        const predecessorKeys = Array.isArray(successor?.entry?.predecessorKeys)
            ? successor.entry.predecessorKeys
            : [];
        predecessorKeys.forEach((predKey) => {
            const predecessor = byKey.get(String(predKey || '').trim());
            if (!predecessor || predecessor.key === successor.key) return;
            const x1 = predecessor.bar.rightPx;
            const y1 = predecessor.rowCenterY;
            const x2 = successor.bar.leftPx;
            const y2 = successor.rowCenterY;
            if (![x1, y1, x2, y2].every(Number.isFinite)) return;
            const midX = x1 + Math.max(12, (x2 - x1) / 2);
            const d = `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
            paths.push({
                key: `${predecessor.key}->${successor.key}`,
                d,
            });
        });
    });

    return paths;
};
